<template>
  <div>
    <div class="d-print-none">
      <p class="fs-lg font-weight-bold">
        <b-icon icon="info-circle" class="mr-1" />
        Je potrebné, aby ste zadali cenu za odobratý plynu a mesačnú platbu
        Vášho dodávateľa, prípadne iné súvisiace náklady vstupujúce do koncovej
        ceny (preprava/skladovanie). Pri zadávaní dodržujte správne jednotky a
        jednotkové ceny.
      </p>
      <p>
        Vašu presnú spotrebu nájdete v aplikácií IS ESM v záložke „Dáta“ –
        fakturačné dáta alebo na ročnom vyúčtovaní Vášho dodávateľa. Ak
        dostávate mesačné vyúčtovanie, nezabudnite spotrebu sčítať za celý
        kalendárny rok.
      </p>
      <p>Kalkulačka počíta vždy s cenami aktuálneho roka.</p>
      <p>
        Odporúčaná výška zálohovej platby je orientačná, stále sa viete dohodnúť
        na výške a počte záloh s Vašim dodávateľom.
      </p>
    </div>
    <b-row>
      <b-col md="6">
        <b-card class="mb-4" no-body title="Vstupné údaje">
          <b-card-body>
            <table
              class="table table-sm table-tp table-borderless table-middle fs-base"
            >
              <thead>
                <tr>
                  <th class="p-0" colspan="2">
                    <h5 class="card-title">Vstupné údaje</h5>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Tarifná skupina</td>
                  <td>
                    <b-form-select
                      v-model.lazy="input.tariff"
                      v-validate="'required'"
                      :class="{ 'is-invalid': errors.has('tariff') }"
                      class="d-print-none"
                      :select-size="1"
                      data-vv-as="Tarifná skupina"
                      name="tariff"
                      @change="inputCalc"
                    >
                      <option :value="null">--Zvolte--</option>
                      <option
                        v-for="(item, key) in selection.tariff"
                        :key="key"
                        :value="key"
                      >
                        {{ item }}
                      </option>
                    </b-form-select>
                    <span class="d-none d-print-block">
                      {{ selection.tariff[input.tariff] }}
                    </span>
                    <b-form-invalid-feedback>
                      {{ errors.first('tariff') }}
                    </b-form-invalid-feedback>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
    <transition mode="out-in" name="fadeUpShort">
      <b-card v-if="input.tariff" class="mb-4" no-body>
        <b-card-body>
          <div class="table-responsive">
            <table class="table table-sm table-tp table-striped table-middle">
              <thead>
                <tr>
                  <th class="pl-0 border-bottom-0" colspan="5">
                    <h5 class="card-title">
                      Fakturované položky za dodávku, prepravu a skladovanie
                      plynu
                    </h5>
                  </th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th style="width: 40%">Položky</th>
                  <th class="text-center">Jednotky</th>
                  <th class="text-right">Počet jednotiek</th>
                  <th class="text-right">Jedn. cena (€ bez dph)</th>
                  <th class="text-right">Suma (€ bez dph)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Sadzba za odob. plyn obchodník "SOPo"</td>
                  <td class="text-center">MWh</td>
                  <td>
                    <vue-autonumeric
                      v-model="items.sopo.amount"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 3,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.sopo.amount | formatNumber(3)
                    }}</span>
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="items.sopo.unit"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 4,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.sopo.unit | formatNumber(4)
                    }}</span>
                  </td>
                  <td class="text-right">
                    <template v-if="items.sopo.total">
                      {{ items.sopo.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>Fixná mesačná platba "FMSo"</td>
                  <td class="text-center">mesiac</td>
                  <td class="text-right">
                    <vue-autonumeric
                      v-model="items.fmso.amount"
                      v-validate="'required|min_value:1|max_value:12'"
                      :class="{ 'is-invalid': errors.has('fmso') }"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 0,
                        maximumValue: 12,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                      data-vv-as="Počet mesiacov"
                      name="fmso"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.fmso.amount | formatNumber
                    }}</span>
                    <b-form-invalid-feedback>
                      {{ errors.first('fmso') }}
                    </b-form-invalid-feedback>
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="items.fmso.unit"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 4,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.fmso.unit | formatNumber(4)
                    }}</span>
                  </td>
                  <td class="text-right">
                    <template v-if="items.fmso.total">
                      {{ items.fmso.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>Sadzba za odob. plyn preprava "SOPp"</td>
                  <td class="text-center">MWh</td>
                  <td class="text-right">
                    <template v-if="items.sopp.amount">
                      {{ items.sopp.amount | formatNumber(3) }}
                    </template>
                    <template v-else> - </template>
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="items.sopp.unit"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 4,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.sopp.unit | formatNumber(4)
                    }}</span>
                  </td>
                  <td class="text-right">
                    <template v-if="items.sopp.total">
                      {{ items.sopp.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>Fixná mesačná platba "FMSp"</td>
                  <td class="text-center">mesiac</td>
                  <td class="text-right">
                    {{ items.fmsp.amount }}
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="items.fmsp.unit"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 4,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.fmsp.unit | formatNumber(4)
                    }}</span>
                  </td>
                  <td class="text-right">
                    <template v-if="items.fmsp.total">
                      {{ items.fmsp.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>Sadzba za odob. plyn skladovanie "SOPs"</td>
                  <td class="text-center">MWh</td>
                  <td class="text-right">
                    <template v-if="items.sops.amount">
                      {{ items.sops.amount | formatNumber(3) }}
                    </template>
                    <template v-else> - </template>
                  </td>
                  <td>
                    <vue-autonumeric
                      v-model="items.sops.unit"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 4,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      items.sops.unit | formatNumber(4)
                    }}</span>
                  </td>
                  <td class="text-right">
                    <template v-if="items.sops.total">
                      {{ items.sops.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>
                    <b-form-checkbox
                      v-model="applyTax"
                      class="d-print-none"
                      title="Zahrnúť do výpočtu"
                      @change="calc"
                    >
                      Spotrebná daň
                    </b-form-checkbox>
                    <div class="text-muted fs-sm">
                      Možnosť nefakturovať v prípade Oslobodenia od spotrebnej
                      dane z elektriny, uhlia a zemného plynu
                    </div>
                  </td>
                  <td class="text-center">MWh</td>
                  <td class="text-right">
                    <template v-if="applyTax && items.consumptionTax.amount">
                      {{ items.consumptionTax.amount | formatNumber(3) }}
                    </template>
                    <template v-else> - </template>
                  </td>
                  <td class="text-right">
                    {{
                      applyTax
                        ? findPrice('consumption_tax')
                        : 0 | formatNumber(4)
                    }}
                  </td>
                  <td class="text-right">
                    <template v-if="items.consumptionTax.total">
                      {{ items.consumptionTax.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr v-if="totalSupply" class="bg-transparent">
                  <td class="text-right" colspan="5">
                    <h5 class="fs-lg text-primary font-weight-bold">
                      <small class="fs-base"
                        >Celkom za dodávku, prepravu, skladovanie plynu (€ bez
                        dph):</small
                      >
                      {{ totalSupply | formatNumber(2) }}
                    </h5>
                  </td>
                </tr>
              </tbody>
              <thead>
                <tr>
                  <th class="pl-0 pt-5 pb-0 border-bottom-0" colspan="5">
                    <h5 class="card-title">
                      Fakturované položky za distribúciu plynu
                    </h5>
                  </th>
                </tr>
              </thead>
              <thead>
                <tr>
                  <th style="width: 40%">Položky</th>
                  <th class="text-center">Jednotky</th>
                  <th class="text-right">Počet jednotiek</th>
                  <th class="text-right">Jedn. cena (€ bez dph)</th>
                  <th class="text-right">Suma (€ bez dph)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Sadzba za odob. plyn distribúcia "SOPd"</td>
                  <td class="text-center">MWh</td>
                  <td class="text-right">
                    <template v-if="secItems.sopd.amount">
                      {{ secItems.sopd.amount | formatNumber(3) }}
                    </template>
                    <template v-else> - </template>
                  </td>
                  <td class="text-right">
                    {{ variableTariff | formatNumber(4) }}
                  </td>
                  <td class="text-right">
                    <template v-if="secItems.sopd.total">
                      {{ secItems.sopd.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr>
                  <td>Fixná mesačná platba "FMSd"</td>
                  <td class="text-center">mesiac</td>
                  <td class="text-right">
                    {{ secItems.fmsd.amount }}
                  </td>
                  <td class="text-right">
                    {{ monthlyTariff | formatNumber(4) }}
                  </td>
                  <td class="text-right">
                    <template v-if="secItems.fmsd.total">
                      {{ secItems.fmsd.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr v-if="!mTariff">
                  <td>Ročná sadzba za DMM</td>
                  <td class="text-center">m3/deň</td>
                  <td>
                    <vue-autonumeric
                      v-model="secItems.yearlyTariff.amount"
                      :options="{
                        decimalCharacter: ',',
                        digitGroupSeparator: ' ',
                        decimalPlaces: 3,
                        emptyInputBehavior: 'null',
                      }"
                      class="form-control form-control-sm text-right d-print-none"
                    />
                    <span class="text-right d-none d-print-block">{{
                      secItems.yearlyTariff.amount | formatNumber(3)
                    }}</span>
                  </td>
                  <td class="text-right">
                    {{ yearlyTariff | formatNumber(4) }}
                  </td>
                  <td class="text-right">
                    <template v-if="secItems.yearlyTariff.total">
                      {{ secItems.yearlyTariff.total | formatNumber(2) }}
                    </template>
                    <template v-else>
                      <b-icon class="text-muted" icon="question-circle" />
                    </template>
                  </td>
                </tr>
                <tr v-if="totalDistribution" class="bg-transparent">
                  <td class="text-right border-0" colspan="5">
                    <h5 class="fs-lg text-primary font-weight-bold">
                      <small class="fs-base"
                        >Celkom za distribúciu plynu (€ bez dph):</small
                      >
                      {{ totalDistribution | formatNumber(2) }}
                    </h5>
                  </td>
                </tr>
                <tr v-if="totalSupplyDist" class="bg-transparent">
                  <td class="text-right pt-3" colspan="5">
                    <h5 class="fs-lg text-primary font-weight-bold">
                      <small class="fs-base">Celkom (€ bez dph):</small>
                      {{ totalSupplyDist | formatNumber(2) }}
                    </h5>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-alert
            v-if="
              (items.fmso.amount == 1 || items.fmso.amount == 12) && recDeposit
            "
            show
            variant="primary"
          >
            <h4 class="font-weight-bold text-center mb-0 final-result">
              Odporúčaná výška mesačnej zálohovej platby s DPH:
              <strong>{{ recDeposit | formatNumber(2) }} €</strong>
            </h4>
          </b-alert>
        </b-card-body>
      </b-card>
    </transition>
  </div>
</template>
<script>
import getPrices from '../mixins/getPrices'

export default {
  mixins: [getPrices],
  props: {
    year: {
      type: [String, Number],
    },
  },
  data() {
    return {
      loading: false,
      pricelist: [],
      input: {
        tariff: null,
      },
      items: {
        sopo: { amount: null, unit: null, total: null },
        fmso: { amount: null, unit: null, total: null },
        sopp: { amount: null, unit: null, total: null },
        fmsp: { amount: null, unit: null, total: null },
        sops: { amount: null, unit: null, total: null },
        consumptionTax: { amount: null, unit: null, total: null },
      },
      secItems: {
        sopd: { amount: null, unit: null, total: null },
        fmsd: { amount: null, unit: null, total: null },
        yearlyTariff: { amount: null, unit: null, total: null },
      },
      totalSupply: null,
      totalDistribution: null,
      totalSupplyDist: null,
      recDeposit: null,
      applyTax: false,
    }
  },
  computed: {
    selection() {
      return {
        tariff: this.tariff,
      }
    },
    tariff() {
      let output = {}
      let m = 1
      for (let i = 0; i < 8; i++) {
        output[`m${m}`] = `M${m}`
        m++
      }
      output['s9'] = 'S9'
      output['s10'] = 'S10'

      let v = 11
      for (let i = 10; i < 26; i++) {
        output[`v${v}`] = `V${v}`
        v++
      }
      return output
    },
    variableTariff() {
      return this.findPrice(`variable_tariff_spp_${this.input.tariff}`)
    },
    monthlyTariff() {
      return this.findPrice(`fix_monthly_tariff_spp_${this.input.tariff}`)
    },
    yearlyTariff() {
      return this.findPrice(`yearly_tariff_spp_${this.input.tariff}`)
    },
    mTariff() {
      return !!this.input.tariff.includes('m')
    },
    tax() {
      return this.year >= 2025 ? 1.23 : 1.2
    },
  },
  watch: {
    items: {
      deep: true,
      handler() {
        this.calc()
      },
    },
    secItems: {
      deep: true,
      handler() {
        this.calc()
      },
    },
    year() {
      this.fetchPrices(true)
    },
  },
  created() {
    this.fetchPrices()
  },
  methods: {
    fetchPrices(calc = false) {
      this.getPrices(
        [
          'yearly_tariff_spp',
          'consumption_tax',
          'fix_monthly_tariff_spp',
          'variable_tariff_spp',
        ],
        calc,
      )
    },
    inputCalc() {
      this.secItems.yearlyTariff.amount = null
      /*if (this.inputReady && (this.totalSupply || this.totalDistribution)) {
        this.resetItems()
      }*/
    },
    calc() {
      let sopo = this.items.sopo.amount
      let fmso = this.items.fmso.amount
      this.items.sopp.amount = sopo
      this.items.sops.amount = sopo
      this.items.consumptionTax.amount = sopo
      this.items.fmsp.amount = fmso
      if (this.applyTax) {
        this.items.consumptionTax.unit = this.findPrice('consumption_tax')
      } else {
        this.items.consumptionTax.unit = 0
      }
      for (let i in this.items) {
        this.items[i].total = this.items[i].amount * this.items[i].unit
      }
      this.totalSupply = Object.keys(this.items).reduce(
        (sum, item) => sum + parseFloat(this.items[item].total || 0),
        0,
      )

      this.secItems.sopd.amount = sopo
      this.secItems.fmsd.amount = fmso
      this.secItems.sopd.unit = this.variableTariff * 1000
      this.secItems.fmsd.unit = this.monthlyTariff
      this.secItems.yearlyTariff.unit = this.yearlyTariff
      for (let i in this.secItems) {
        if (i === 'yearlyTariff') {
          this.secItems.yearlyTariff.total =
            ((this.secItems.yearlyTariff.amount *
              this.secItems.yearlyTariff.unit) /
              12) *
            fmso
        } else {
          this.secItems[i].total =
            this.secItems[i].amount * this.secItems[i].unit
        }
      }
      this.totalDistribution = Object.keys(this.secItems).reduce(
        (sum, item) => sum + parseFloat(this.secItems[item].total || 0),
        0,
      )

      this.totalSupplyDist = this.totalSupply + this.totalDistribution
      this.recDeposit = (this.totalSupplyDist / fmso) * this.tax
    },
    findPrice(name) {
      let price = this.pricelist.find((price) => price.name === name)
      if (price) {
        return price.values?.value
      }
    },
    reset() {
      Object.assign(this.$data, this.$options.data())
      this.fetchPrices()
    },
    resetItems() {
      Object.assign(this.items, this.$options.data().items)
    },
  },
}
</script>
